import { directSupabase } from '../lib/supabaseClient';
import { JobActuals, JobActualsInput } from '../types/jobActuals.types';

function fromDbJobActuals(row: any): JobActuals {
  return {
    id: row.id,
    projectId: row.project_id,
    invoiceId: row.invoice_id,
    actualLabourHours: Number(row.actual_labour_hours || 0),
    actualLabourCost: Number(row.actual_labour_cost || 0),
    actualLabour: Array.isArray(row.actual_labour) ? row.actual_labour : [],
    actualMaterials: Array.isArray(row.actual_materials) ? row.actual_materials : [],
    actualMaterialsCost: Number(row.actual_materials_cost || 0),
    actualEquipment: Array.isArray(row.actual_equipment) ? row.actual_equipment : [],
    actualEquipmentCost: Number(row.actual_equipment_cost || 0),
    actualSubcontractorCosts: Array.isArray(row.actual_subcontractor_costs) ? row.actual_subcontractor_costs : [],
    actualSubcontractorTotal: Number(row.actual_subcontractor_total || 0),
    actualAdditionalCosts: Array.isArray(row.actual_additional_costs) ? row.actual_additional_costs : [],
    actualAdditionalTotal: Number(row.actual_additional_total || 0),
    actualTotalCost: Number(row.actual_total_cost || 0),
    actualProfit: Number(row.actual_profit || 0),
    actualBaseCostTotal: row.actual_base_cost_total != null ? Number(row.actual_base_cost_total) : null,
    actualAdminFee: row.actual_admin_fee != null ? Number(row.actual_admin_fee) : null,
    actualContingency: row.actual_contingency != null ? Number(row.actual_contingency) : null,
    actualProfitMargin: row.actual_profit_margin != null ? Number(row.actual_profit_margin) : null,
    actualVatAmount: row.actual_vat_amount != null ? Number(row.actual_vat_amount) : null,
    notes: row.notes || null,
    createdBy: row.created_by || null,
    updatedBy: row.updated_by || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toDbJobActuals(input: JobActualsInput): any {
  // Calculate totals
  const materialsCost = input.actualMaterialsCost || 
    (input.actualMaterials || []).reduce((sum, m) => sum + (m.total || 0), 0);
  const equipmentCost = input.actualEquipmentCost || 
    (input.actualEquipment || []).reduce((sum, e) => sum + (e.total || 0), 0);
  const subcontractorTotal = input.actualSubcontractorTotal || 
    (input.actualSubcontractorCosts || []).reduce((sum, s) => sum + (s.amount || 0), 0);
  const additionalTotal = input.actualAdditionalTotal || 
    (input.actualAdditionalCosts || []).reduce((sum, a) => sum + (a.amount || 0), 0);
  
  const labourCost = input.actualLabourCost || 0;
  const totalCost = labourCost + materialsCost + equipmentCost + subcontractorTotal + additionalTotal;

  return {
    project_id: input.projectId,
    invoice_id: input.invoiceId,
    actual_labour_hours: input.actualLabourHours || 0,
    actual_labour_cost: labourCost,
    actual_labour: input.actualLabour || [],
    actual_materials: input.actualMaterials || [],
    actual_materials_cost: materialsCost,
    actual_equipment: input.actualEquipment || [],
    actual_equipment_cost: equipmentCost,
    actual_subcontractor_costs: input.actualSubcontractorCosts || [],
    actual_subcontractor_total: subcontractorTotal,
    actual_additional_costs: input.actualAdditionalCosts || [],
    actual_additional_total: additionalTotal,
    actual_total_cost: totalCost,
    actual_base_cost_total: input.actualBaseCostTotal != null ? Number(input.actualBaseCostTotal) : null,
    actual_admin_fee: input.actualAdminFee != null ? Number(input.actualAdminFee) : null,
    actual_contingency: input.actualContingency != null ? Number(input.actualContingency) : null,
    actual_profit_margin: input.actualProfitMargin != null ? Number(input.actualProfitMargin) : null,
    actual_vat_amount: input.actualVatAmount != null ? Number(input.actualVatAmount) : null,
    notes: input.notes || null,
  };
}

export const jobActualsService = {
  /**
   * Get job actuals by project and invoice
   */
  async getByProjectAndInvoice(projectId: string, invoiceId: string): Promise<JobActuals | null> {
    const { data, error } = await directSupabase
      .from('job_actuals')
      .select('*')
      .eq('project_id', projectId)
      .eq('invoice_id', invoiceId)
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data ? fromDbJobActuals(data) : null;
  },

  /**
   * Get all job actuals for a project
   */
  async getByProject(projectId: string): Promise<JobActuals[]> {
    const { data, error } = await directSupabase
      .from('job_actuals')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(fromDbJobActuals);
  },

  /**
   * Get all job actuals for an invoice
   */
  async getByInvoice(invoiceId: string): Promise<JobActuals[]> {
    const { data, error } = await directSupabase
      .from('job_actuals')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(fromDbJobActuals);
  },

  /**
   * Create or update job actuals
   * Note: actual_profit is calculated separately based on invoice revenue
   */
  async upsert(input: JobActualsInput): Promise<JobActuals> {
    // First, get the invoice to calculate profit
    const { data: invoiceData, error: invoiceError } = await directSupabase
      .from('documents')
      .select('gross_total, net_total, vat_amount')
      .eq('id', input.invoiceId)
      .single();

    if (invoiceError) throw new Error(`Failed to fetch invoice: ${invoiceError.message}`);

    const revenue = Number(
      invoiceData?.net_total ??
      invoiceData?.gross_total ??
      0
    );
    const dbInput = toDbJobActuals(input);
    const actualProfit = revenue - dbInput.actual_total_cost;
    dbInput.actual_profit = actualProfit;

    // Check if record exists
    const existing = await this.getByProjectAndInvoice(input.projectId, input.invoiceId);

    if (existing) {
      // Update
      const { data, error } = await directSupabase
        .from('job_actuals')
        .update(dbInput)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return fromDbJobActuals(data);
    } else {
      // Insert
      const { data, error } = await directSupabase
        .from('job_actuals')
        .insert(dbInput)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return fromDbJobActuals(data);
    }
  },

  /**
   * Delete job actuals
   */
  async delete(id: string): Promise<void> {
    const { error } = await directSupabase
      .from('job_actuals')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  },
};

